import React, {useEffect, useState} from "react";
import {Checkbox, Input} from "antd";
import Modal from "@iso/components/Feedback/Modal";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Removes HTML markup that company names can contain (they are rendered with
// dangerouslySetInnerHTML elsewhere) so we can safely prefill the input.
const stripHtml = (value) => (value || "").toString().replace(/<[^>]*>/g, "").trim();

/**
 * Dialog that collects the orderer's contact data (Name, Firmenname, E-Mail)
 * plus an explicit order confirmation. Shown when an offer/order is accepted
 * without a recognised cockpit session, so that we know who placed the order
 * and can write it to Salesforce. The confirm button stays disabled until all
 * fields are filled and the e-mail is valid.
 */
export default function OrderContactDialog({open, loading, defaultCompany, onConfirm, onCancel}) {
  const [name, setName] = useState("");
  const [company, setCompany] = useState("");
  const [email, setEmail] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [touched, setTouched] = useState({});

  // Reset the form (and prefill the known company) every time the dialog opens.
  useEffect(() => {
    if (open) {
      setName("");
      setCompany(stripHtml(defaultCompany));
      setEmail("");
      setConfirmed(false);
      setTouched({});
    }
  }, [open, defaultCompany]);

  const errors = {
    name: !name.trim(),
    company: !company.trim(),
    email: !EMAIL_REGEX.test(email.trim()),
    confirmed: !confirmed,
  };
  const isValid = !Object.values(errors).some(Boolean);

  const markTouched = (field) => setTouched((prev) => ({...prev, [field]: true}));

  const handleOk = () => {
    if (!isValid) {
      return;
    }
    onConfirm({
      name: name.trim(),
      company: company.trim(),
      email: email.trim(),
    });
  };

  const errorStyle = {color: "red", fontSize: 12, marginTop: 4};

  return (
    <Modal
      open={open}
      title={"Ihre Kontaktdaten zur Bestellung"}
      confirmLoading={loading}
      okText={"Kostenpflichtig bestellen"}
      cancelText={"Abbrechen"}
      okButtonProps={{disabled: !isValid}}
      onOk={handleOk}
      onCancel={loading ? undefined : onCancel}
      maskClosable={!loading}
      closable={!loading}
    >
      <div style={{marginBottom: 16}}>
        Bitte geben Sie uns ein paar Informationen, damit wir die Bestellung durchführen können.
      </div>
      <div>
        <label>Name *</label>
        <Input value={name} disabled={loading} onChange={(e) => setName(e.target.value)} onBlur={() => markTouched("name")} />
        {touched.name && errors.name && <div style={errorStyle}>Bitte geben Sie Ihren Namen an.</div>}
      </div>
      <div style={{marginTop: 12}}>
        <label>Firmenname *</label>
        <Input value={company} disabled={loading} onChange={(e) => setCompany(e.target.value)} onBlur={() => markTouched("company")} />
        {touched.company && errors.company && <div style={errorStyle}>Bitte geben Sie Ihren Firmennamen an.</div>}
      </div>
      <div style={{marginTop: 12}}>
        <label>E-Mail *</label>
        <Input type={"email"} value={email} disabled={loading} onChange={(e) => setEmail(e.target.value)} onBlur={() => markTouched("email")} />
        {touched.email && errors.email && <div style={errorStyle}>Bitte geben Sie eine gültige E-Mail-Adresse an.</div>}
      </div>
      <div style={{marginTop: 16}}>
        <label style={{cursor: "pointer"}}>
          <Checkbox checked={confirmed} disabled={loading} onChange={(e) => setConfirmed(e.target.checked)} />
          <span style={{paddingLeft: 8}}>
            Ich möchte diese Bestellung verbindlich und kostenpflichtig aufgeben.
          </span>
        </label>
      </div>
    </Modal>
  );
}
